import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI, clearBrowserApiCache, getErrorMessage, refreshAccessToken, setAuthHeader } from '../services/api';
import { canUserAccess } from '../utils/permissions';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isBootstrapping: true,
      authChecked: false,
      error: null,

      login: async (username, password, loginContext = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login(username, password, loginContext);
          const { access, refresh, user } = response.data;
          
          localStorage.setItem('access_token', access);
          localStorage.setItem('refresh_token', refresh);
          setAuthHeader(access);
          
          set({ 
            user, 
            isAuthenticated: true, 
            isLoading: false,
            isBootstrapping: false,
            authChecked: true,
          });
          
          return { success: true };
        } catch (error) {
          const message = getErrorMessage(error, 'خطا در ورود به سیستم');
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        try {
          authAPI.logout();
        } catch (error) {
          // Session cleanup on the server is best-effort only.
        }
        clearBrowserApiCache();
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('auth-storage');
        setAuthHeader(null);
        set({ user: null, isAuthenticated: false, isBootstrapping: false, authChecked: true, error: null });
      },

      fetchProfile: async () => {
        try {
          const response = await authAPI.getProfile();
          set({
            user: response.data,
            isAuthenticated: true,
            isBootstrapping: false,
            authChecked: true,
          });
          return response.data;
        } catch (error) {
          const status = error?.response?.status;
          const isAuthError = status === 401 || status === 403;
          console.error('Failed to fetch profile:', error);

          if (isAuthError) {
            clearBrowserApiCache();
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('auth-storage');
            setAuthHeader(null);
            set({
              user: null,
              isAuthenticated: false,
              isBootstrapping: false,
              authChecked: true,
              error: null,
            });
          } else {
            set({
              isBootstrapping: false,
              authChecked: true,
            });
          }
          throw error;
        }
      },

      initializeAuth: async () => {
        const accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!accessToken && !refreshToken) {
          setAuthHeader(null);
          set({
            user: null,
            isAuthenticated: false,
            isBootstrapping: false,
            authChecked: true,
          });
          return null;
        }

        set({ isBootstrapping: true, authChecked: false });

        try {
          let access = accessToken;
          if (!access && refreshToken) {
            access = await refreshAccessToken();
          }
          setAuthHeader(access || localStorage.getItem('access_token'));
          return await get().fetchProfile();
        } catch (error) {
          const status = error?.response?.status;
          const isAuthError = status === 401 || status === 403;
          if (isAuthError) {
            get().logout();
          } else {
            set({ isBootstrapping: false, authChecked: true });
          }
          return null;
        }
      },

      updateProfile: async (data) => {
        try {
          const response = await authAPI.updateProfile(data);
          set({ user: response.data });
          return { success: true };
        } catch (error) {
          return { 
            success: false, 
            error: error.response?.data || 'خطا در بروزرسانی پروفایل' 
          };
        }
      },

      hasPermission: (permission) => {
        const { user } = get();
        return canUserAccess(user, permission);
      },

      isAdmin: () => {
        const { user } = get();
        return !!user?.is_admin || user?.role === 'admin';
      },

      isVet: () => {
        const { user } = get();
        return canUserAccess(user, ['can_create_treatment', 'treatments.edit']);
      },

      canExecuteInjection: () => {
        const { user } = get();
        return canUserAccess(user, 'can_execute_injection');
      },

      canManageProcurement: () => {
        const { user } = get();
        return canUserAccess(user, 'can_manage_procurement');
      },

      canManageInventoryMasterData: () => {
        const { user } = get();
        return canUserAccess(user, 'can_manage_inventory_master_data');
      },

      canManageInventoryDocuments: () => {
        const { user } = get();
        return canUserAccess(user, 'can_manage_inventory_documents');
      },

      canPostInventoryDocuments: () => {
        const { user } = get();
        return canUserAccess(user, 'can_post_inventory_documents');
      },

      canReverseInventoryDocuments: () => {
        const { user } = get();
        return canUserAccess(user, 'can_reverse_inventory_documents');
      },

      canImportInventoryDocuments: () => {
        const { user } = get();
        return canUserAccess(user, 'can_import_inventory_documents');
      },

      canViewInventoryReports: () => {
        const { user } = get();
        return canUserAccess(user, 'can_view_inventory_reports');
      },

      canManageFinancialData: () => {
        const { user } = get();
        return canUserAccess(user, 'can_manage_financial_data');
      },

      canViewManagementReports: () => {
        const { user } = get();
        return canUserAccess(user, 'can_view_management_reports');
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

export default useAuthStore;
